import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Box } from '@mui/material';
import { resolveIdDisplay } from '../../utils/itemId';

/**
 * IdChip — design-language display for an item identifier (DESIGN_LANGUAGE.md V9).
 * Human IDs (REQ-0001) render in full; long raw UUIDs truncate to `7ac718a3…`
 * in a monospace sunken chip, copy-on-click, full value in the tooltip. Never
 * wraps, never link-soup.
 *
 * When `showHumanIds` is passed (issue #174 Part 1b), the primary/secondary form
 * is resolved via the shared `resolveIdDisplay` helper — honouring the user's
 * friendly-id/raw-UUID preference and revealing the OTHER form in the tooltip —
 * using `id` (+ optional `displayId`/`humanId`) as the item's identifiers.
 * Callers that don't pass `showHumanIds` keep the original ad-hoc truncation
 * behaviour, unchanged, for backward compatibility.
 */
function IdChip({ id, displayId, humanId, showHumanIds, title, onClick, sx, disableTooltip = false }) {
  if (!id) return null;
  const raw = String(id);

  let label;
  let tooltipRaw;
  let copyValue;
  let looksUuid;
  if (showHumanIds !== undefined) {
    const item = { id, display_id: displayId, human_id: humanId };
    const ids = resolveIdDisplay(item, showHumanIds);
    label = ids.primary;
    tooltipRaw = ids.secondary ? `${ids.primary} · ${ids.secondary}` : ids.primary;
    copyValue = ids.primaryIsRaw ? raw : (ids.secondary || raw);
    looksUuid = ids.primaryIsRaw;
  } else {
    looksUuid = raw.length > 14 && /[0-9a-f]/i.test(raw) && !/^[A-Z]+-\d+$/i.test(raw);
    label = looksUuid ? `${raw.slice(0, 8)}…` : raw;
    tooltipRaw = raw;
    copyValue = raw;
  }

  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (navigator?.clipboard && looksUuid) navigator.clipboard.writeText(copyValue).catch(() => { /* ignore clipboard errors */ });
  };

  // When wrapped by the advanced ItemTooltip, suppress the built-in MUI tooltip so
  // they don't double-stack (the advanced mini already shows the detail).
  return (
    <Tooltip title={disableTooltip ? '' : (title ? `${title} · ${tooltipRaw}` : tooltipRaw)} arrow disableHoverListener={disableTooltip}>
      <Box
        component="span"
        onClick={handleClick}
        sx={{
          fontFamily: '"IBM Plex Mono", ui-monospace, Menlo, monospace',
          fontSize: 11,
          lineHeight: '18px',
          bgcolor: 'action.hover',
          color: 'text.secondary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px',
          px: 0.75,
          py: '1px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          display: 'inline-block',
          maxWidth: '100%',
          '&:hover': { color: 'text.primary', borderColor: 'text.disabled' },
          ...sx,
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

IdChip.propTypes = {
  id: PropTypes.string,
  // Optional friendly-id sources (used only when `showHumanIds` is passed).
  displayId: PropTypes.string,
  humanId: PropTypes.string,
  showHumanIds: PropTypes.bool,
  title: PropTypes.string,
  onClick: PropTypes.func,
  sx: PropTypes.object,
  disableTooltip: PropTypes.bool,
};

export default IdChip;
